import {
  CustomNumberFormat,
  CustomNumberFormatJSON,
  MutableCustomNumberFormat,
  NumberSeparator,
} from '@databases/model/custom-number-format';
import * as Y from 'yjs';

import { SynchronizedRecord } from './helpers/synchronized-record';

const CustomNumberFormatProperties = {
  minFractionDigits: 'mifd',
  maxFractionDigits: 'mafd',
  thousandsSeparator: 'ts',
  decimalSeparator: 'ds',
  prefix: 'p',
  suffix: 's',
};

enum YNumberSeparator {
  COMMA = 'c',
  DOT = 'd',
  WHITESPACE = 'w',
  APOSTROPHE = 'a',
}
const YNumberSeparatorPossibleValues = Object.values(YNumberSeparator);

const NumberSeparatorMap: Record<NumberSeparator, YNumberSeparator> = {
  [NumberSeparator.COMMA]: YNumberSeparator.COMMA,
  [NumberSeparator.DOT]: YNumberSeparator.DOT,
  [NumberSeparator.WHITESPACE]: YNumberSeparator.WHITESPACE,
  [NumberSeparator.APOSTROPHE]: YNumberSeparator.APOSTROPHE,
};

const YNumberSeparatorMap: Record<YNumberSeparator, NumberSeparator> = {
  [YNumberSeparator.COMMA]: NumberSeparator.COMMA,
  [YNumberSeparator.DOT]: NumberSeparator.DOT,
  [YNumberSeparator.WHITESPACE]: NumberSeparator.WHITESPACE,
  [YNumberSeparator.APOSTROPHE]: NumberSeparator.APOSTROPHE,
};

export class YCustomNumberFormat
  extends SynchronizedRecord
  implements CustomNumberFormat, MutableCustomNumberFormat
{
  constructor(data: Y.Map<unknown>, attach?: (data: Y.Map<any>) => void) {
    super(data, attach);
  }

  get minFractionDigits(): number | undefined {
    return this.getNumber(CustomNumberFormatProperties.minFractionDigits, undefined);
  }

  set minFractionDigits(val: number | undefined) {
    this.set(CustomNumberFormatProperties.minFractionDigits, val);
  }

  get maxFractionDigits(): number | undefined {
    return this.getNumber(CustomNumberFormatProperties.maxFractionDigits, undefined);
  }

  set maxFractionDigits(val: number | undefined) {
    this.set(CustomNumberFormatProperties.maxFractionDigits, val);
  }

  get decimalSeparator(): NumberSeparator {
    const yNumberSeparator = this.getStringEnum<YNumberSeparator[]>(
      CustomNumberFormatProperties.decimalSeparator,
      YNumberSeparatorPossibleValues,
      YNumberSeparator.DOT
    );

    return YNumberSeparatorMap[yNumberSeparator];
  }

  set decimalSeparator(val: NumberSeparator) {
    const yNumberSeparator = NumberSeparatorMap[val];
    this.set(CustomNumberFormatProperties.decimalSeparator, yNumberSeparator);
  }

  get thousandsSeparator(): NumberSeparator | undefined {
    const yNumberSeparator = this.getStringEnumOptional(
      CustomNumberFormatProperties.thousandsSeparator,
      YNumberSeparatorPossibleValues,
      undefined
    );

    return yNumberSeparator ? YNumberSeparatorMap[yNumberSeparator] : undefined;
  }

  set thousandsSeparator(val: NumberSeparator | undefined) {
    if (val !== undefined) {
      const yNumberSeparator = NumberSeparatorMap[val];
      this.set(CustomNumberFormatProperties.thousandsSeparator, yNumberSeparator);
    } else {
      this.set(CustomNumberFormatProperties.thousandsSeparator, undefined);
    }
  }

  get prefix(): string | undefined {
    return this.getString(CustomNumberFormatProperties.prefix, undefined);
  }

  set prefix(val: string | undefined) {
    this.set(CustomNumberFormatProperties.prefix, val);
  }

  get suffix(): string | undefined {
    return this.getString(CustomNumberFormatProperties.suffix, undefined);
  }

  set suffix(val: string | undefined) {
    this.set(CustomNumberFormatProperties.suffix, val);
  }

  toJSON(): CustomNumberFormatJSON {
    return {
      minFractionDigits: this.minFractionDigits,
      maxFractionDigits: this.maxFractionDigits,
      thousandsSeparator: this.thousandsSeparator,
      decimalSeparator: this.decimalSeparator,
      prefix: this.prefix,
      suffix: this.suffix,
    };
  }

  static fromJSON(value: CustomNumberFormatJSON | undefined): YCustomNumberFormat {
    return new YCustomNumberFormat(
      new Y.Map([
        [CustomNumberFormatProperties.minFractionDigits, value?.minFractionDigits],
        [CustomNumberFormatProperties.maxFractionDigits, value?.maxFractionDigits],
        [
          CustomNumberFormatProperties.thousandsSeparator,
          value?.thousandsSeparator
            ? NumberSeparatorMap[value?.thousandsSeparator]
            : undefined,
        ],
        [CustomNumberFormatProperties.prefix, value?.prefix],
        [CustomNumberFormatProperties.suffix, value?.suffix],
      ])
    );
  }
}
